const ALLOWED_BLOB_OPEN_PROTOCOLS = new Set(['blob:']);

function normalizeStatementPdfBlob(blob: Blob): Blob | null {
  if (blob.type === 'application/pdf') {
    return blob;
  }
  if (blob.type === '' || blob.type === 'application/octet-stream') {
    return new Blob([blob], { type: 'application/pdf' });
  }
  return null;
}

/**
 * Creates an object URL suitable for opening a downloaded statement PDF in a new tab.
 * Returns null when the blob or resulting URL is not safe to navigate to.
 */
export function createSafePdfBlobObjectUrl(blob: Blob): string | null {
  const pdfBlob = normalizeStatementPdfBlob(blob);
  if (!pdfBlob) {
    return null;
  }

  const objectUrl = URL.createObjectURL(pdfBlob);
  try {
    const parsed = new URL(objectUrl);
    if (!ALLOWED_BLOB_OPEN_PROTOCOLS.has(parsed.protocol)) {
      URL.revokeObjectURL(objectUrl);
      return null;
    }
    return parsed.href;
  } catch {
    URL.revokeObjectURL(objectUrl);
    return null;
  }
}

/** Opens a PDF blob in a new browsing context without passing remote URLs to window.open. */
export function openPdfBlobInNewTab(blob: Blob): void {
  const safeHref = createSafePdfBlobObjectUrl(blob);
  if (!safeHref) {
    console.error('[openPdfBlobInNewTab]: Refusing to open invalid statement blob');
    return;
  }

  const anchor = document.createElement('a');
  anchor.href = safeHref;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  anchor.click();

  window.setTimeout(() => URL.revokeObjectURL(safeHref), 60_000);
}
